/**
 * 폴리곤 좌표로부터 이미지 속성 계산 유틸리티
 * 폴리곤 좌표 배열을 기반으로 이미지의 center, rotation, scale 값을 계산
 */

export const EARTH_RADIUS = 6371000; // 지구 반지름 (미터)

// 좌표 추출 유틸리티
export const coordinateUtils = {
  /**
   * 폴리곤 좌표에서 이미지의 4개 모서리 좌표 추출
   * @param {Array} polygonCoordinates - polygon 좌표 배열
   * @returns {Object} 4개 모서리 좌표 {topLeft, topRight, bottomRight, bottomLeft}
   */
  extractImageCorners(polygonCoordinates) {
    if (!polygonCoordinates || polygonCoordinates.length < 4) {
      return null;
    }

    // 좌표들을 위도 기준으로 정렬하여 상하 구분
    const sortedByLat = [...polygonCoordinates].sort((a, b) => b[1] - a[1]);
    const topCoords = sortedByLat.slice(0, 2);
    const bottomCoords = sortedByLat.slice(2, 4);

    // 경도 기준으로 정렬하여 좌우 구분
    const topLeft = topCoords.sort((a, b) => a[0] - b[0])[0];
    const topRight = topCoords.sort((a, b) => a[0] - b[0])[1];
    const bottomLeft = bottomCoords.sort((a, b) => a[0] - b[0])[0];
    const bottomRight = bottomCoords.sort((a, b) => a[0] - b[0])[1];

    return {
      topLeft,
      topRight,
      bottomRight,
      bottomLeft
    };
  },

  /**
   * 두 지리 좌표 간의 거리 계산 (미터 단위)
   * @param {Array} coord1 - 첫 번째 좌표 [longitude, latitude]
   * @param {Array} coord2 - 두 번째 좌표 [longitude, latitude]
   * @returns {number} 거리 (미터)
   */
  calculateDistance(coord1, coord2) {
    const [lng1, lat1] = coord1;
    const [lng2, lat2] = coord2;

    const lat1Rad = lat1 * Math.PI / 180;
    const lat2Rad = lat2 * Math.PI / 180;
    const deltaLat = (lat2 - lat1) * Math.PI / 180;
    const deltaLng = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
              Math.cos(lat1Rad) * Math.cos(lat2Rad) *
              Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS * c;
  },

  /**
   * 폴리곤 좌표로부터 이미지의 경계 상자(bounding box) 계산
   * @param {Array} polygonCoordinates - polygon 좌표 배열
   * @returns {Object} 경계 상자 {minLng, maxLng, minLat, maxLat}
   */
  calculateBoundingBox(polygonCoordinates) {
    if (!polygonCoordinates || polygonCoordinates.length === 0) {
      return null;
    }

    const lngs = polygonCoordinates.map(coord => coord[0]);
    const lats = polygonCoordinates.map(coord => coord[1]);

    return {
      minLng: Math.min(...lngs),
      maxLng: Math.max(...lngs),
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats)
    };
  },

  /**
   * 좌표가 polygon 내부에 있는지 확인
   * @param {Array} point - 확인할 좌표 [longitude, latitude]
   * @param {Array} polygonCoordinates - polygon 좌표 배열
   * @returns {boolean} 내부 여부
   */
  isPointInPolygon(point, polygonCoordinates) {
    if (!point || !polygonCoordinates || polygonCoordinates.length < 3) {
      return false;
    }

    const [x, y] = point;
    let inside = false;

    for (let i = 0, j = polygonCoordinates.length - 1; i < polygonCoordinates.length; j = i++) {
      const [xi, yi] = polygonCoordinates[i];
      const [xj, yj] = polygonCoordinates[j];

      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }

    return inside;
  }
};

// 이미지 속성 계산 유틸리티
export const imagePropertyUtils = {
  /**
   * 폴리곤 좌표로부터 이미지 중심점 계산
   * @param {Array} polygonCoordinates - polygon 좌표 배열
   * @returns {Array} 중심점 좌표 [longitude, latitude]
   */
  calculateImageCenter(polygonCoordinates) {
    if (!polygonCoordinates || polygonCoordinates.length === 0) {
      console.error('[polygonToImageCalculator] 유효하지 않은 폴리곤 좌표');
      return null;
    }

    const sumLng = polygonCoordinates.reduce((sum, coord) => sum + coord[0], 0);
    const sumLat = polygonCoordinates.reduce((sum, coord) => sum + coord[1], 0);

    const center = [
      sumLng / polygonCoordinates.length,
      sumLat / polygonCoordinates.length
    ];

    console.log('[polygonToImageCalculator] 이미지 중심점 계산:', {
      polygonCoordinates: polygonCoordinates.length,
      center: center
    });

    return center;
  },

  /**
   * 폴리곤 좌표로부터 이미지 회전 각도 계산
   * @param {Array} polygonCoordinates - polygon 좌표 배열
   * @returns {number} 회전 각도 (라디안)
   */
  calculateImageRotation(polygonCoordinates) {
    if (!polygonCoordinates || polygonCoordinates.length < 2) {
      console.error('[polygonToImageCalculator] 유효하지 않은 폴리곤 좌표');
      return 0;
    }

    const corners = coordinateUtils.extractImageCorners(polygonCoordinates);
    if (!corners) {
      console.error('[polygonToImageCalculator] 모서리 좌표 추출 실패');
      return 0;
    }

    // 상단 경계의 각도 계산 (topLeft에서 topRight로의 방향)
    const deltaLng = corners.topRight[0] - corners.topLeft[0];
    const deltaLat = corners.topRight[1] - corners.topLeft[1];

    const rotationAngle = -Math.atan2(deltaLat, deltaLng);

    console.log('[polygonToImageCalculator] 이미지 회전 각도 계산:', {
      topLeft: corners.topLeft,
      topRight: corners.topRight,
      deltaLng: deltaLng,
      deltaLat: deltaLat,
      rotationAngle: rotationAngle,
      rotationAngleDegrees: rotationAngle * 180 / Math.PI
    });

    return rotationAngle;
  },

  /**
   * 폴리곤 좌표로부터 이미지 스케일 계산
   * @param {Array} polygonCoordinates - polygon 좌표 배열
   * @param {Object} imageSize - 이미지 크기 {width, height}
   * @returns {Array} 스케일 값 [scaleX, scaleY]
   */
  calculateImageScale(polygonCoordinates, imageSize) {
    if (!polygonCoordinates || !imageSize) {
      console.error('[polygonToImageCalculator] 유효하지 않은 입력 데이터');
      return [1, 1];
    }

    const corners = coordinateUtils.extractImageCorners(polygonCoordinates);
    if (!corners) {
      console.error('[polygonToImageCalculator] 모서리 좌표 추출 실패');
      return [1, 1];
    }

    // 실제 지리적 거리 계산 (미터 단위)
    const widthDistance = coordinateUtils.calculateDistance(corners.topLeft, corners.topRight);
    const heightDistance = coordinateUtils.calculateDistance(corners.topLeft, corners.bottomLeft);

    // 픽셀당 미터 비율 계산 (스케일)
    const scaleX = widthDistance / imageSize.width;
    const scaleY = heightDistance / imageSize.height;

    console.log('[polygonToImageCalculator] 이미지 스케일 계산:', {
      imageSize: imageSize,
      widthDistance: widthDistance,
      heightDistance: heightDistance,
      scaleX: scaleX,
      scaleY: scaleY,
      aspectRatio: scaleX / scaleY
    });

    return [scaleX, scaleY];
  },

  /**
   * 폴리곤 좌표로부터 이미지의 모든 속성 계산 (통합 함수)
   * @param {Array} polygonCoordinates - polygon 좌표 배열
   * @param {Object} imageSize - 이미지 크기 {width, height}
   * @returns {Object} 이미지 속성 {center, rotation, scale}
   */
  calculateImageProperties(polygonCoordinates, imageSize) {
    if (!polygonCoordinates || !imageSize) {
      console.error('[polygonToImageCalculator] 유효하지 않은 입력 데이터');
      return null;
    }

    console.log('[polygonToImageCalculator] 이미지 속성 계산 시작:', {
      polygonCoordinates: polygonCoordinates.length,
      imageSize: imageSize
    });

    // 각 속성 계산
    const center = this.calculateImageCenter(polygonCoordinates);
    const rotation = this.calculateImageRotation(polygonCoordinates);
    const scale = this.calculateImageScale(polygonCoordinates, imageSize);

    if (!center) {
      console.error('[polygonToImageCalculator] 중심점 계산 실패');
      return null;
    }

    const properties = {
      center: center,
      rotation: rotation,
      scale: scale
    };

    console.log('[polygonToImageCalculator] 이미지 속성 계산 완료:', {
      center: center,
      rotation: rotation,
      rotationDegrees: rotation * 180 / Math.PI,
      scale: scale
    });

    return properties;
  }
};

// 에러 처리 유틸리티
export const errorUtils = {
  handleError(error, context) {
    console.error(`Error in ${context}:`, error);
    return {
      success: false,
      error: error.message,
      context
    };
  },

  validatePolygonCoordinates(polygonCoordinates) {
    if (!Array.isArray(polygonCoordinates) || polygonCoordinates.length < 3) {
      throw new Error('Invalid polygon coordinates: must be an array with at least 3 points');
    }

    polygonCoordinates.forEach((coord, index) => {
      if (!Array.isArray(coord) || coord.length !== 2) {
        throw new Error(`Invalid coordinate at index ${index}: must be [lon, lat]`);
      }

      const [lon, lat] = coord;
      if (typeof lon !== 'number' || typeof lat !== 'number') {
        throw new Error(`Invalid coordinate values at index ${index}: must be numbers`);
      }

      if (lon < -180 || lon > 180) {
        throw new Error(`Invalid longitude at index ${index}: must be between -180 and 180`);
      }

      if (lat < -90 || lat > 90) {
        throw new Error(`Invalid latitude at index ${index}: must be between -90 and 90`);
      }
    });

    return true;
  },

  validateImageSize(imageSize) {
    if (!imageSize || typeof imageSize !== 'object') {
      throw new Error('Invalid image size: must be an object');
    }

    if (typeof imageSize.width !== 'number' || typeof imageSize.height !== 'number') {
      throw new Error('Invalid image size: width and height must be numbers');
    }

    if (imageSize.width <= 0 || imageSize.height <= 0) {
      throw new Error('Invalid image size: width and height must be greater than 0');
    }

    return true;
  }
};
